import * as React from 'react'
import { createContext, useContext } from 'react'
const generateId = () => Math.floor(Math.random() * 2_147_483_647)
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { AccomodationModel } from '@/app/models/newDelegation/accomodationModel'
import { AllowanceModel } from '@/app/models/newDelegation/allowanceModel'
import { InvoiceModel } from '@/app/models/newDelegation/invoiceModel'
import { AdvancePaymentModel } from '@/app/models/newDelegation/advancePaymentModel'

interface NewTripContextProps {
  // Events
  events: EventModel[]
  addEvent: (event: EventModel) => void
  removeEvents: (events: EventModel[]) => void
  editEvent: (event: EventModel) => void
  moveEvent: (event: EventModel, up: boolean) => void
  resetEvents: (events: EventModel[]) => void
  hideDelegationMarkerPopups?: (event: EventModel) => void
  markNewEvent?: (event: EventModel) => void

  // Accommodations
  accommodations: AccomodationModel[]
  addAccommodation: (accommodation: AccomodationModel) => void
  removeAccommodations: (accommodations: AccomodationModel[]) => void
  editAccommodation: (accommodation: AccomodationModel) => void
  resetAccommodations: (accommodations: AccomodationModel[]) => void

  // Allowances
  allowances: AllowanceModel[]
  addAllowance: (allowance: AllowanceModel) => void
  removeAllowances: (allowances: AllowanceModel[]) => void
  editAllowance: (allowance: AllowanceModel) => void
  resetAllowances: (allowances: AllowanceModel[]) => void

  // Invoices
  invoices: InvoiceModel[]
  addInvoice: (invoice: InvoiceModel) => void
  removeInvoices: (invoices: InvoiceModel[]) => void
  editInvoice: (invoice: InvoiceModel) => void
  resetInvoices: (invoices: InvoiceModel[]) => void

  // Advance Payments
  advancePayments: AdvancePaymentModel[]
  addAdvancePayment: (advancePayment: AdvancePaymentModel) => void
  removeAdvancePayments: (advancePayments: AdvancePaymentModel[]) => void
  editAdvancePayment: (advancePayment: AdvancePaymentModel) => void
  resetAdvancePayments: (advancePayments: AdvancePaymentModel[]) => void

  // State
  submitted: boolean
  setSubmitted: (submitted: boolean) => void
  reset: () => void
  submit: () => void
}

const NewTripContext = createContext<NewTripContextProps | null>(null)

export const NewTripProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [events, setEvents] = React.useState<EventModel[]>([])
  const [accommodations, setAccommodations] = React.useState<
    AccomodationModel[]
  >([])
  const [allowances, setAllowances] = React.useState<AllowanceModel[]>([])
  const [invoices, setInvoices] = React.useState<InvoiceModel[]>([])
  const [advancePayments, setAdvancePayments] = React.useState<
    AdvancePaymentModel[]
  >([])
  const [submitted, setSubmitted] = React.useState<boolean>(false)

  const isSameEvent = React.useCallback(
    (a: EventModel, b: EventModel): boolean => {
      if (a.id && b.id) return a.id === b.id
      return (
        a.lp === b.lp &&
        a.tripEvent === b.tripEvent &&
        String(a.date) === String(b.date) &&
        (a.country?.code ?? '') === (b.country?.code ?? '')
      )
    },
    [],
  )

  const addEvent = React.useCallback((event: EventModel) => {
    const eventWithId = event.id ? event : { ...event, id: generateId() }
    setEvents((prev) => [...prev, eventWithId])
  }, [])

  const removeEvents = React.useCallback(
    (eventsToRemove: EventModel[]) => {
      setEvents((prev) =>
        prev.filter((e) => !eventsToRemove.some((er) => isSameEvent(er, e))),
      )
    },
    [isSameEvent],
  )

  const editEvent = React.useCallback(
    (event: EventModel) => {
      setEvents((prev) => prev.map((e) => (isSameEvent(e, event) ? event : e)))
    },
    [isSameEvent],
  )

  const moveEvent = React.useCallback(
    (event: EventModel, up: boolean) => {
      setEvents((prev) => {
        const index = prev.findIndex((e) => isSameEvent(e, event))
        if (index === -1) return prev
        if (up && index === 0) return prev
        if (!up && index === prev.length - 1) return prev

        const newEvents = [...prev]
        const targetIndex = up ? index - 1 : index + 1
        ;[newEvents[index], newEvents[targetIndex]] = [
          newEvents[targetIndex],
          newEvents[index],
        ]
        return newEvents
      })
    },
    [isSameEvent],
  )

  const resetEvents = React.useCallback((newEvents: EventModel[]) => {
    setEvents(newEvents)
  }, [])

  const hideDelegationMarkerPopups = React.useCallback(
    (event: EventModel) => {
      // Implementation depends on event structure
      // For now, just update the event
      editEvent(event)
    },
    [editEvent],
  )

  const markNewEvent = React.useCallback(
    (event: EventModel) => {
      // Mark event as new - implementation depends on event structure
      editEvent(event)
    },
    [editEvent],
  )

  const addAccommodation = React.useCallback(
    (accommodation: AccomodationModel) => {
      const withId = accommodation.id
        ? accommodation
        : { ...accommodation, id: generateId() }
      setAccommodations((prev) => [...prev, withId])
    },
    [],
  )

  const removeAccommodations = React.useCallback(
    (accommodationsToRemove: AccomodationModel[]) => {
      setAccommodations((prev) =>
        prev.filter(
          (a) => !accommodationsToRemove.some((ar) => ar.id === a.id),
        ),
      )
    },
    [],
  )

  const editAccommodation = React.useCallback(
    (accommodation: AccomodationModel) => {
      setAccommodations((prev) =>
        prev.map((a) => (a.id === accommodation.id ? accommodation : a)),
      )
    },
    [],
  )

  const resetAccommodations = React.useCallback(
    (newAccommodations: AccomodationModel[]) => {
      setAccommodations(newAccommodations)
    },
    [],
  )

  const addAllowance = React.useCallback((allowance: AllowanceModel) => {
    const withId = allowance.id ? allowance : { ...allowance, id: generateId() }
    setAllowances((prev) => [...prev, withId])
  }, [])

  const removeAllowances = React.useCallback(
    (allowancesToRemove: AllowanceModel[]) => {
      setAllowances((prev) =>
        prev.filter((a) => !allowancesToRemove.some((ar) => ar.id === a.id)),
      )
    },
    [],
  )

  const editAllowance = React.useCallback((allowance: AllowanceModel) => {
    setAllowances((prev) =>
      prev.map((a) => (a.id === allowance.id ? allowance : a)),
    )
  }, [])

  const resetAllowances = React.useCallback(
    (newAllowances: AllowanceModel[]) => {
      setAllowances(newAllowances)
    },
    [],
  )

  const addInvoice = React.useCallback((invoice: InvoiceModel) => {
    const withId = invoice.id ? invoice : { ...invoice, id: generateId() }
    setInvoices((prev) => [...prev, withId])
  }, [])

  const removeInvoices = React.useCallback(
    (invoicesToRemove: InvoiceModel[]) => {
      setInvoices((prev) =>
        prev.filter((i) => !invoicesToRemove.some((ir) => ir.id === i.id)),
      )
    },
    [],
  )

  const editInvoice = React.useCallback((invoice: InvoiceModel) => {
    setInvoices((prev) => prev.map((i) => (i.id === invoice.id ? invoice : i)))
  }, [])

  const resetInvoices = React.useCallback((newInvoices: InvoiceModel[]) => {
    setInvoices(newInvoices)
  }, [])

  const addAdvancePayment = React.useCallback(
    (advancePayment: AdvancePaymentModel) => {
      const withId = advancePayment.id
        ? advancePayment
        : { ...advancePayment, id: generateId() }
      setAdvancePayments((prev) => [...prev, withId])
    },
    [],
  )

  const removeAdvancePayments = React.useCallback(
    (advancePaymentsToRemove: AdvancePaymentModel[]) => {
      setAdvancePayments((prev) =>
        prev.filter(
          (ap) => !advancePaymentsToRemove.some((apr) => apr.id === ap.id),
        ),
      )
    },
    [],
  )

  const editAdvancePayment = React.useCallback(
    (advancePayment: AdvancePaymentModel) => {
      setAdvancePayments((prev) =>
        prev.map((ap) => (ap.id === advancePayment.id ? advancePayment : ap)),
      )
    },
    [],
  )

  const resetAdvancePayments = React.useCallback(
    (newAdvancePayments: AdvancePaymentModel[]) => {
      setAdvancePayments(newAdvancePayments)
    },
    [],
  )

  const reset = React.useCallback(() => {
    setEvents([])
    setAccommodations([])
    setAllowances([])
    setInvoices([])
    setAdvancePayments([])
    setSubmitted(false)
  }, [])

  const submit = React.useCallback(() => {
    setSubmitted(true)
  }, [])

  return (
    <NewTripContext.Provider
      value={{
        events,
        addEvent,
        removeEvents,
        editEvent,
        moveEvent,
        resetEvents,
        hideDelegationMarkerPopups,
        markNewEvent,
        accommodations,
        addAccommodation,
        removeAccommodations,
        editAccommodation,
        resetAccommodations,
        allowances,
        addAllowance,
        removeAllowances,
        editAllowance,
        resetAllowances,
        invoices,
        addInvoice,
        removeInvoices,
        editInvoice,
        resetInvoices,
        advancePayments,
        addAdvancePayment,
        removeAdvancePayments,
        editAdvancePayment,
        resetAdvancePayments,
        submitted,
        setSubmitted,
        reset,
        submit,
      }}
    >
      {children}
    </NewTripContext.Provider>
  )
}

export const useNewTrip = () => {
  const ctx = useContext(NewTripContext)
  if (!ctx) {
    throw new Error('useNewTrip must be used inside <NewTripProvider>')
  }
  return ctx
}
